import { useEffect, useRef } from 'react';

const useLobbySocket = (lobbyID, onMessage, onStatusChange, onReconnect, onTerminated) => {
    const socketRef = useRef(null);
    const onMessageRef = useRef(onMessage);
    const onStatusRef = useRef(onStatusChange);
    const onReconnectRef = useRef(onReconnect);
    const onTerminatedRef = useRef(onTerminated);
    const reconnectAttempt = useRef(0);
    const shouldReconnect = useRef(true);
    const reconnectTimer = useRef(null);
    const hasConnected = useRef(false);

    useEffect(() => { onMessageRef.current = onMessage; });
    useEffect(() => { onStatusRef.current = onStatusChange; });
    useEffect(() => { onReconnectRef.current = onReconnect; });
    useEffect(() => { onTerminatedRef.current = onTerminated; });

    useEffect(() => {
        if (!lobbyID) return;
        shouldReconnect.current = true;
        reconnectAttempt.current = 0;
        hasConnected.current = false;

        const connect = () => {
            const ws = new WebSocket(`${process.env.REACT_APP_WS_API_URL}/ws/lobby/${lobbyID}/`);
            socketRef.current = ws;

            ws.onopen = () => {
                hasConnected.current = true;
                const wasReconnect = reconnectAttempt.current > 0;
                reconnectAttempt.current = 0;
                onStatusRef.current?.(true);
                if (wasReconnect) onReconnectRef.current?.();
            };
            ws.onmessage = (e) => {
                try {
                    onMessageRef.current(JSON.parse(e.data));
                } catch (err) {
                    console.error('Lobby WebSocket: failed to parse message', e.data, err);
                }
            };
            ws.onerror = (e) => console.error('Lobby WebSocket error:', e);
            ws.onclose = () => {
                if (!shouldReconnect.current) return;
                if (hasConnected.current) onStatusRef.current?.(false);
                if (reconnectAttempt.current >= 5) {
                    onTerminatedRef.current?.();
                    return;
                }
                const delay = Math.min(1000 * 2 ** reconnectAttempt.current, 30000);
                reconnectAttempt.current += 1;
                reconnectTimer.current = setTimeout(connect, delay);
            };
        };

        connect();

        return () => {
            shouldReconnect.current = false;
            clearTimeout(reconnectTimer.current);
            socketRef.current?.close();
        };
    }, [lobbyID]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useLobbySocket;
